import axios from "@lib/axios";
import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

interface Props {
  pathName: string;
  queryKeyToInvalidate?: string | QueryKey | QueryKey[];
  onSuccess?: (data: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
}

interface MutationParams {
  id: string;
}

export const useRemoveResource = ({
  pathName, 
  queryKeyToInvalidate,
  onSuccess,
  onError,
}: Props) => {
  const queryClient = useQueryClient();
  const { mutate: onRemoveResource, ...rest } = useMutation({
    mutationFn: ({ id }: MutationParams) => axios.delete(`${pathName}/${id}`),
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
      if (queryKeyToInvalidate) {
        // If it's a string, convert to array format
        if (typeof queryKeyToInvalidate === 'string') {
          queryClient.invalidateQueries({ queryKey: [queryKeyToInvalidate] });
        }
        // If it's an array, check if all elements are strings (multiple keys)
        // or if it contains non-strings (single composite key)
        else if (Array.isArray(queryKeyToInvalidate)) {
          const isMultipleSimpleKeys = queryKeyToInvalidate.every(
            (key) => typeof key === 'string'
          );
          
          if (isMultipleSimpleKeys) {
            // Multiple simple keys: invalidate each separately
            queryKeyToInvalidate.forEach((key) => {
              queryClient.invalidateQueries({ queryKey: [key] });
            });
          } else {
            // Single composite key: use as-is
            queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });
          }
        }
        // If it's already a QueryKey (array with non-string elements), use as-is
        else {
          queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });
        }
      }
    },
    onError,
  });

  return { onRemoveResource, ...rest };
};
